import type {
  CreateOffboardingBody,
  OffboardingCase,
  OffboardingsList,
  OffboardingsQuery,
} from "@evolua/api-client";

const COMPANY = "aaaaaaaa-aaaa-4aaa-8aaa-aaaaaaaaaaaa";
const ANA = "11111111-1111-4111-8111-111111111111";

function defaultChecklist(): OffboardingCase["checklist"] {
  return [
    {
      key: "collect_badge",
      label: "Recolher crachá e acessos físicos",
      required: true,
      ownerRole: "FACILITIES",
      status: "PENDING",
      completedBy: null,
      completedAt: null,
      note: null,
    },
    {
      key: "return_equipment",
      label: "Conferir devolução de equipamentos",
      required: true,
      ownerRole: "IT",
      status: "PENDING",
      completedBy: null,
      completedAt: null,
      note: null,
    },
    {
      key: "exit_interview",
      label: "Entrevista de desligamento",
      required: true,
      ownerRole: "HR",
      status: "PENDING",
      completedBy: null,
      completedAt: null,
      note: null,
    },
    {
      key: "payroll_final",
      label: "Verificar rescisão / folha final",
      required: true,
      ownerRole: "FINANCE",
      status: "PENDING",
      completedBy: null,
      completedAt: null,
      note: null,
    },
    {
      key: "revoke_systems",
      label: "Revogar acessos de sistemas",
      required: true,
      ownerRole: "IT",
      status: "PENDING",
      completedBy: null,
      completedAt: null,
      note: null,
    },
  ];
}

function defaultAssets(): OffboardingCase["assets"] {
  return [
    {
      id: crypto.randomUUID(),
      label: "Notebook corporativo",
      status: "PENDING",
      returnedAt: null,
      receivedBy: null,
      note: null,
    },
    {
      id: crypto.randomUUID(),
      label: "Crachá / cartão de acesso",
      status: "PENDING",
      returnedAt: null,
      receivedBy: null,
      note: null,
    },
  ];
}

function refresh(row: OffboardingCase): void {
  if (
    row.status === "ACCESS_REVOKED" ||
    row.status === "COMPLETED" ||
    row.status === "CANCELLED"
  ) {
    return;
  }
  const checklistOk = row.checklist
    .filter((i) => i.required)
    .every((i) => i.status === "DONE" || i.status === "WAIVED");
  const assetsOk = row.assets.every((a) => a.status !== "PENDING");
  const any =
    row.checklist.some((i) => i.status !== "PENDING") ||
    row.assets.some((a) => a.status !== "PENDING") ||
    Boolean(row.interview);
  if (checklistOk && assetsOk) row.status = "READY_TO_REVOKE";
  else if (any) row.status = "IN_PROGRESS";
  else row.status = "DRAFT";
}

function clone(row: OffboardingCase): OffboardingCase {
  return {
    ...row,
    checklist: row.checklist.map((i) => ({ ...i })),
    assets: row.assets.map((a) => ({ ...a })),
    interview: row.interview
      ? {
          ...row.interview,
          answers: row.interview.answers.map((a) => ({ ...a })),
        }
      : null,
    access: { ...row.access },
    retention: { ...row.retention },
  };
}

const cases: OffboardingCase[] = [
  {
    id: "ffffffff-ffff-4fff-8fff-ffffffffff01",
    companyId: COMPANY,
    employeeId: ANA,
    employeeName: "Ana Silva",
    lastDay: "2026-08-29",
    reason: "VOLUNTARY",
    status: "DRAFT",
    checklistTemplateId: "ffffffff-ffff-4fff-8fff-ffffffffff10",
    checklist: defaultChecklist(),
    assets: defaultAssets(),
    interview: null,
    access: {
      revokedAt: null,
      revokedBy: null,
      platformSubject: "demo-employee",
      employeeMarkedTerminated: false,
      userStatusAfter: "ACTIVE",
    },
    retention: {
      policyRef: "docs/lgpd/retencao.md",
      archivedAt: null,
      hardDeleteForbidden: true,
      dataDisposition: "RETAINED",
    },
    progressPercent: 0,
    createdAt: new Date().toISOString(),
    createdBy: "demo-hr",
    completedAt: null,
  },
];

export function listDemoHrOffboardings(query: OffboardingsQuery = {}): OffboardingsList {
  let data = cases.map(clone);
  if (query.status) data = data.filter((c) => c.status === query.status);
  if (query.q?.trim()) {
    const q = query.q.trim().toLowerCase();
    data = data.filter((c) => c.employeeName.toLowerCase().includes(q));
  }
  return { data, total: data.length };
}

export function createDemoHrOffboarding(body: CreateOffboardingBody): OffboardingCase {
  const row: OffboardingCase = {
    id: crypto.randomUUID(),
    companyId: body.companyId,
    employeeId: body.employeeId,
    employeeName: body.employeeId === ANA ? "Ana Silva" : "Colaborador",
    lastDay: body.lastDay,
    reason: body.reason ?? "VOLUNTARY",
    status: "DRAFT",
    checklistTemplateId:
      body.checklistTemplateId ?? "ffffffff-ffff-4fff-8fff-ffffffffff10",
    checklist: defaultChecklist(),
    assets: defaultAssets(),
    interview: null,
    access: {
      revokedAt: null,
      revokedBy: null,
      platformSubject: body.employeeId === ANA ? "demo-employee" : null,
      employeeMarkedTerminated: false,
      userStatusAfter: "ACTIVE",
    },
    retention: {
      policyRef: "docs/lgpd/retencao.md",
      archivedAt: null,
      hardDeleteForbidden: true,
      dataDisposition: "RETAINED",
    },
    progressPercent: 0,
    createdAt: new Date().toISOString(),
    createdBy: "demo-hr",
    completedAt: null,
  };
  cases.unshift(row);
  return clone(row);
}

export function getDemoHrOffboarding(id: string): OffboardingCase {
  const row = cases.find((c) => c.id === id);
  if (!row) throw new Error("OFFBOARDING_NOT_FOUND");
  return clone(row);
}

export function completeDemoHrOffboardingItem(
  id: string,
  key: string,
  body: { note?: string; waive?: boolean } = {},
): OffboardingCase {
  const row = cases.find((c) => c.id === id);
  if (!row) throw new Error("OFFBOARDING_NOT_FOUND");
  const item = row.checklist.find((i) => i.key === key);
  if (!item) throw new Error("OFFBOARDING_ITEM_NOT_FOUND");
  item.status = body.waive ? "WAIVED" : "DONE";
  item.completedBy = "demo-hr";
  item.completedAt = new Date().toISOString();
  item.note = body.note ?? null;
  refresh(row);
  return clone(row);
}

export function returnDemoHrOffboardingAsset(
  id: string,
  assetId: string,
  body: { status?: "RETURNED" | "LOST" | "WAIVED"; note?: string } = {},
): OffboardingCase {
  const row = cases.find((c) => c.id === id);
  if (!row) throw new Error("OFFBOARDING_NOT_FOUND");
  const asset = row.assets.find((a) => a.id === assetId);
  if (!asset) throw new Error("OFFBOARDING_ASSET_NOT_FOUND");
  asset.status = body.status ?? "RETURNED";
  asset.returnedAt = new Date().toISOString();
  asset.receivedBy = "demo-hr";
  asset.note = body.note ?? null;
  refresh(row);
  return clone(row);
}

export function saveDemoHrOffboardingInterview(
  id: string,
  body: { answers: Array<{ key: string; question: string; answer: string }> },
): OffboardingCase {
  const row = cases.find((c) => c.id === id);
  if (!row) throw new Error("OFFBOARDING_NOT_FOUND");
  row.interview = {
    conductedAt: new Date().toISOString(),
    conductedBy: "demo-hr",
    answers: body.answers.map((a) => ({ ...a })),
  };
  const item = row.checklist.find((i) => i.key === "exit_interview");
  if (item && item.status === "PENDING") {
    item.status = "DONE";
    item.completedBy = "demo-hr";
    item.completedAt = row.interview.conductedAt;
  }
  refresh(row);
  return clone(row);
}

export function revokeDemoHrOffboardingAccess(id: string): OffboardingCase {
  const row = cases.find((c) => c.id === id);
  if (!row) throw new Error("OFFBOARDING_NOT_FOUND");
  const checklistOk = row.checklist
    .filter((i) => i.required)
    .every((i) => i.status === "DONE" || i.status === "WAIVED");
  const assetsOk = row.assets.every((a) => a.status !== "PENDING");
  if (!checklistOk || !assetsOk) throw new Error("OFFBOARDING_NOT_READY");
  row.access.revokedAt = new Date().toISOString();
  row.access.revokedBy = "demo-hr";
  row.access.employeeMarkedTerminated = true;
  row.access.userStatusAfter = "DISABLED";
  row.status = "ACCESS_REVOKED";
  return clone(row);
}

export function completeDemoHrOffboarding(id: string): OffboardingCase {
  const row = cases.find((c) => c.id === id);
  if (!row) throw new Error("OFFBOARDING_NOT_FOUND");
  if (row.status !== "ACCESS_REVOKED") {
    revokeDemoHrOffboardingAccess(id);
  }
  const current = cases.find((c) => c.id === id)!;
  current.status = "COMPLETED";
  current.completedAt = new Date().toISOString();
  current.retention.archivedAt = current.completedAt;
  current.retention.dataDisposition = "RETAINED";
  return clone(current);
}
